import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaPlus, FaTrash, FaCalendarAlt, FaExclamationTriangle } from 'react-icons/fa';

/**
 * Sub-editor INLINE per la child table SKE_CampiOperatori.
 * Usato esclusivamente dalla modale HUB (`CampiTableModalHub`).
 *
 * Differenze rispetto al `CampiOperatoriEditor` (modale separata, fuori dal
 * modulo Hub): qui le righe vivono dentro lo stato del form padre, vengono
 * passate al salvataggio della modale principale e persistite nella stessa
 * transazione del campo.
 *
 * Props:
 *  - value: array di righe { COP_Operatore, COP_SuffissoFiltro, COP_Sequenza, COP_Id_CampiOperatori? }
 *  - onChange(newRows)
 *  - cercabile: bool. Quando false mostra solo warning se ci sono righe esistenti.
 *  - readOnlyOperatori?: bool — riservato a futuri usi.
 */

export const OPERATORI_VALIDI = ['eq', 'like', 'in', 'gte', 'lte', 'gt', 'lt'];

const dupKey = (op, suff) => `${op}|${(suff ?? '').trim().toLowerCase()}`;

const CampiOperatoriSubEditor = ({ value = [], onChange, cercabile }) => {
    const rows = Array.isArray(value) ? value : [];

    // Calcolo righe duplicate (per evidenziazione UI). La validazione
    // bloccante avviene comunque sul submit della modale padre + hook backend.
    const duplicateIdx = useMemo(() => {
        const seen = new Map();
        const dups = new Set();
        rows.forEach((r, i) => {
            const k = dupKey(r.COP_Operatore || '', r.COP_SuffissoFiltro || '');
            if (!r.COP_Operatore) return;
            if (seen.has(k)) {
                dups.add(seen.get(k));
                dups.add(i);
            } else {
                seen.set(k, i);
            }
        });
        return dups;
    }, [rows]);

    const nextSeq = useCallback(() => {
        const max = rows.reduce((m, r) => Math.max(m, Number(r.COP_Sequenza) || 0), 0);
        return max + 10;
    }, [rows]);

    const updateRow = (idx, patch) => {
        const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
        onChange(next);
    };

    const addRow = (preset = {}) => {
        onChange([
            ...rows,
            {
                COP_Operatore: preset.COP_Operatore || 'eq',
                COP_SuffissoFiltro: preset.COP_SuffissoFiltro || '',
                COP_Sequenza: preset.COP_Sequenza ?? nextSeq(),
            },
        ]);
    };

    const removeRow = (idx) => {
        const next = rows.filter((_, i) => i !== idx);
        onChange(next);
    };

    const addRangeDataPreset = () => {
        const baseSeq = nextSeq();
        onChange([
            ...rows,
            { COP_Operatore: 'gte', COP_SuffissoFiltro: '_da', COP_Sequenza: baseSeq },
            { COP_Operatore: 'lte', COP_SuffissoFiltro: '_a', COP_Sequenza: baseSeq + 10 },
        ]);
    };

    // Quando il campo non è cercabile, nascondi la tabella ma mostra warning
    // se c'erano già righe persistite.
    if (!cercabile) {
        if (rows.length === 0) return null;
        return (
            <div className="mt-3 rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700 p-3 text-sm text-yellow-800 dark:text-yellow-200 flex items-start space-x-2">
                <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
                <div>
                    Il campo non è cercabile: gli operatori esistenti restano nel DB
                    ({rows.length}) ma non vengono emessi nei template Milo.
                    Cancellali manualmente se non servono più.
                </div>
            </div>
        );
    }

    return (
        <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                    Operatori filtro <span className="text-red-500">*</span>
                </span>
                <div className="flex space-x-2">
                    <button
                        type="button"
                        onClick={() => addRow()}
                        className="inline-flex items-center text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                        <FaPlus className="mr-1" /> Aggiungi riga
                    </button>
                    <button
                        type="button"
                        onClick={addRangeDataPreset}
                        title="Aggiunge gte/_da + lte/_a"
                        className="inline-flex items-center text-xs px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                        <FaCalendarAlt className="mr-1" /> Preset Range Data
                    </button>
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="text-xs text-red-600 dark:text-red-400 py-2">
                    Un campo cercabile deve esporre almeno un operatore.
                </div>
            ) : (
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-xs uppercase text-gray-500 dark:text-gray-400">
                            <th className="text-left py-1 pr-2">Operatore *</th>
                            <th className="text-left py-1 pr-2">Suffisso</th>
                            <th className="text-left py-1 pr-2 w-24">Sequenza *</th>
                            <th className="w-8" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => {
                            const isDup = duplicateIdx.has(i);
                            return (
                                <tr key={i} className={isDup ? 'bg-red-50 dark:bg-red-900/20' : ''}>
                                    <td className="py-1 pr-2">
                                        <select
                                            value={r.COP_Operatore || ''}
                                            onChange={(e) => updateRow(i, { COP_Operatore: e.target.value })}
                                            className={`w-full px-2 py-1 rounded border text-sm bg-white dark:bg-gray-700 dark:text-gray-100 ${isDup ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                                            title={isDup ? 'Coppia (operatore, suffisso) duplicata' : ''}
                                        >
                                            <option value="">--</option>
                                            {OPERATORI_VALIDI.map((op) => (
                                                <option key={op} value={op}>{op}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="py-1 pr-2">
                                        <input
                                            type="text"
                                            maxLength={20}
                                            placeholder="_da, _a, _dopo, _prima o vuoto"
                                            value={r.COP_SuffissoFiltro || ''}
                                            onChange={(e) => updateRow(i, { COP_SuffissoFiltro: e.target.value })}
                                            className={`w-full px-2 py-1 rounded border text-sm bg-white dark:bg-gray-700 dark:text-gray-100 ${isDup ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                                        />
                                    </td>
                                    <td className="py-1 pr-2">
                                        <input
                                            type="number"
                                            value={r.COP_Sequenza ?? ''}
                                            onChange={(e) => updateRow(i, { COP_Sequenza: e.target.value === '' ? '' : Number(e.target.value) })}
                                            className="w-full px-2 py-1 rounded border text-sm bg-white dark:bg-gray-700 dark:text-gray-100 border-gray-300 dark:border-gray-600"
                                        />
                                    </td>
                                    <td className="py-1">
                                        <button
                                            type="button"
                                            onClick={() => removeRow(i)}
                                            className="text-red-500 hover:text-red-700 p-1"
                                            title="Rimuovi riga"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default CampiOperatoriSubEditor;
